import * as THREE from "three";
import {MathUtils, Ray, Vector3} from "three";
import Hammer from 'hammerjs';

const _changeEvent = {type: "change"};
const _objectHoverEvent = {type: "object_hover"};
const _objectClickEvent = {type: "object_click"};
const _objectApproachEvent = {type: "object_approach"};
const _objectApproachClickEvent = {type: "object_approach_click"};

class MouseEventUtils {
    static LEFT_BTN = 0;
    static RIGHT_BTN = 1;
    static WHEEL = 2;

    static isPressed(event, button) {
        return (event.buttons >> button) % 2 !== 0;
    }
}

class BaseControls extends THREE.EventDispatcher {
    constructor(canvasDomElement) {
        super();
        this.registered = false;
        this.canvasDomElement = canvasDomElement;

        this._events = [
            ["mousemove", this._handleMouseMove.bind(this)],
            ["wheel", this._handleMouseWheel.bind(this)],
            ["mousedown", this._handleMouseDown.bind(this)],
            ["mouseup", this._handleMouseUp.bind(this)],
        ]

        this._hammer_events = [
            ["panstart pinchstart rotatestart", event => {
                this._lastPinchScale = event.scale;
                this._lastRotation = event.rotation;
                this._lastDeltaX = event.deltaX;
                this._lastDeltaY = event.deltaY;
            }],
            ["panmove pinchmove rotatemove", this._handleTouch.bind(this)],
        ]

        this._hammer = null;
        this._dragDistance = null;

        this._lastPinchScale = null;
        this._lastRotation = null;
        this._lastDeltaX = null;
        this._lastDeltaY = null;
    }

    register() {
        console.assert(!this.registered);
        for (let [name, handler] of this._events) {
            this.canvasDomElement.addEventListener(name, handler, {passive: false});
        }
        this._hammer = new Hammer(this.canvasDomElement, {inputClass: Hammer.TouchInput});
        this._hammer.get("pan").set({direction: Hammer.DIRECTION_ALL});
        this._hammer.get("pinch").set({enable: true});
        this._hammer.get("rotate").set({enable: true, threshold: 20});
        for (let [name, handler] of this._hammer_events) {
            this._hammer.on(name, handler);
        }

        this.registered = true;
    }

    unregister() {
        console.assert(this.registered);
        for (let [name, handler] of this._events) {
            this.canvasDomElement.removeEventListener(name, handler, {passive: false});
        }
        for (let [name, handler] of this._hammer_events) {
            this._hammer.off(name, handler);
        }
        this._hammer.destroy();
        this._hammer = null;
        this.registered = false;
    }

    _handleMouseMove(event) {
        if (this._dragDistance !== null) {
            const [deltaX, deltaY] = this._getMovement(event);
            this._dragDistance[0] += deltaX;
            this._dragDistance[1] += deltaY;
        }
    }

    _handleMouseWheel(event) {
        // To be overridden.
    }

    _handleMouseDown(event) {
        this._dragDistance = [0, 0];
    }

    _handleMouseUp(event) {
        const dragDistance = this._dragDistance === null ?
            0 :
            Math.sqrt(this._dragDistance[0] ** 2 + this._dragDistance[1] ** 2);
        if (dragDistance < 8) {
            this._handleClick(event);
        }
    }

    _handleClick(event) {
        // To be overridden.
    }

    // TODO: when touch changes from 1-finger to 2-finger, everything freezes
    _handleTouch(event) {
        event.preventDefault();
        event.scaleDelta = event.scale / this._lastPinchScale;
        const rotationDiff = event.rotation - this._lastRotation;
        event.rotationDelta = Math.sign(rotationDiff) * Math.min(Math.abs(rotationDiff), 360 - Math.abs(rotationDiff));
        event.deltaXAdjusted = event.deltaX - this._lastDeltaX;
        event.deltaYAdjusted = event.deltaY - this._lastDeltaY;

        this._lastPinchScale = event.scale;
        this._lastRotation = event.rotation;
        this._lastDeltaX = event.deltaX;
        this._lastDeltaY = event.deltaY;

        // To be overridden.
    }

    cancelAllListeners() {
        this._listeners = undefined;
    }

    _getMovement(event, factor = 1.0) {
        const movementX = event.movementX || event.velocityX || event.mozMovementX || event.webkitMovementX || 0;
        const movementY = event.movementY || event.velocityY || event.mozMovementY || event.webkitMovementY || 0;
        return [movementX * factor, movementY * factor];
    }

    _forwardVector() {
        const forward = new THREE.Vector3();
        this.camera.getWorldDirection(forward);
        return forward;
    }

    _localCoordinateSystem() {
        const globalUp = THREE.Object3D.DEFAULT_UP;
        const forward = this._forwardVector();
        const right = new THREE.Vector3().crossVectors(globalUp, forward).normalize();
        const up = (new THREE.Vector3().subVectors(globalUp, globalUp.clone().projectOnVector(forward))).normalize();

        return [right, up, forward];
    }
}

class TopDownControlsV2 extends BaseControls {
    constructor(
        canvasDomElement,
        camera,
        cameraAnimation,
    ) {
        super(canvasDomElement);
        this.camera = camera;
        this.cameraAnimation = cameraAnimation;
    }

    register(initialTarget = null, animate = true) {
        super.register();
        // TODO
    }

    _handleMouseMove(event) {
        super._handleMouseMove(event);
        if (this.cameraAnimation.isPlaying())
            return;
        if (MouseEventUtils.isPressed(event, MouseEventUtils.RIGHT_BTN) ||
            (MouseEventUtils.isPressed(event, MouseEventUtils.LEFT_BTN) && event.altKey)) {
            const [deltaX, deltaY] = this._getMovement(event);
            this._handleOrbiting(this._getMovement(event))
            // this._handleVerticalOrbiting(deltaY);
        } else if (MouseEventUtils.isPressed(event, MouseEventUtils.LEFT_BTN)) {
            this._handlePanning(this._getMovement(event));
        } else {
            return;
        }

        event.preventDefault()
        this.dispatchEvent(_changeEvent);
    }

    _handleMouseWheel(event) {
        super._handleMouseWheel(event);
        if (this.cameraAnimation.isPlaying())
            return;

        event.preventDefault();
        this._handleZoom(event.deltaY);
        this.dispatchEvent(_changeEvent);
    }

    // _handleTouch(event) {
    //     super._handleTouch(event);
    //     if (this.cameraAnimation.isPlaying())
    //         return;
    //
    //     if (event.pointers.length === 1) {
    //         this._handlePanning([event.deltaXAdjusted * 3, event.deltaYAdjusted * 3]);
    //         // TODO: why should this be here?
    //         // this._handleOrbiting([event.rotationDelta * -5, 0]);
    //     } else {
    //         this._handlePanning([event.deltaXAdjusted * 3, 0]);
    //         this._handleOrbiting([event.rotationDelta * -5, event.deltaYAdjusted * 3]);
    //     }
    //
    //     event.preventDefault();
    //     this.dispatchEvent(_changeEvent);
    // }

    _handlePanning(movement) {
        const [deltaX, deltaY] = movement;

        const globalUp = THREE.Object3D.DEFAULT_UP;
        const forward = this._horizontalForwardVector();
        const right = new THREE.Vector3().crossVectors(globalUp, forward).normalize();

        this.camera.position
            .addScaledVector(right, 0.005 * deltaX)
            .addScaledVector(forward, 0.005 * deltaY);
    }

    _handleOrbiting(movement) {
        const [deltaX, deltaY] = movement;
        const position = this.camera.position;
        const target = this._getOrbitTarget();

        const quat = new THREE.Quaternion().setFromUnitVectors(this.camera.up, new Vector3(0, 1, 0));
        const quatInverse = quat.clone().invert();
        const offset = new THREE.Vector3().subVectors(position, target)
        // rotate offset to "y-axis-is-up" space
        offset.applyQuaternion(quat);

        const spherical = new THREE.Spherical();
        spherical.setFromVector3(offset);

        spherical.theta += -deltaX * 0.003;
        spherical.phi += -deltaY * 0.003;
        spherical.makeSafe();

        offset.setFromSpherical(spherical);
        // rotate offset back to "camera-up-vector-is-up" space
        offset.applyQuaternion(quatInverse);
        position.copy(target).add(offset);
        this.camera.lookAt(target);
        // console.log(this._getCameraAngle())
        // position.setY(5);
    }

    _handleZoom(delta) {
        const position = this.camera.position;
        const target = this._getOrbitTarget();

        const offset = new THREE.Vector3().subVectors(position, target);
        const zoomDirection = offset.clone().normalize();
        const zoomScale = MathUtils.clamp(offset.length(), 1, 10);
        position.add(zoomDirection.multiplyScalar(delta * zoomScale * 0.001));
    }

    _getOrbitTarget() {
        const scale = this.camera.position.y
        return this.camera.position.clone().add(this._forwardVector().multiplyScalar(5));
    }

    _getHorizontalOrbitTarget() {
        const forward = this._horizontalForwardVector();
        const orbitTarget = this.camera.position.clone().add(forward.multiplyScalar(5));
        orbitTarget.y = 0;
        return orbitTarget;
    }

    _horizontalForwardVector() {
        const direction = new THREE.Vector3();
        this.camera.getWorldDirection(direction);
        direction.y = 0;
        return direction.normalize();
    }

    _getCameraAngle() {
        const vector = new THREE.Vector3();
        this.camera.getWorldDirection(vector);
        const horizontalLength = Math.sqrt(vector.x ** 2 + vector.z ** 2);
        return MathUtils.radToDeg(Math.atan2(horizontalLength, vector.y)) - 90;
    }

    _handleVerticalOrbiting(delta) {
        const [minAngle, maxAngle] = [30, 110]
        const [minHeight, maxHeight] = [1.0, 6.0];

        const position = this.camera.position;
        let angle = this._getCameraAngle();

        console.log(angle)
        angle = MathUtils.clamp(angle + delta * 0.1, minAngle, maxAngle);
        const height = minHeight + (maxHeight - minHeight) * (angle - minAngle) / (maxAngle - minAngle);
        const targetHeight = height - 5 * Math.tan(THREE.MathUtils.degToRad(angle));

        const target = this.camera.position.clone().add(this._horizontalForwardVector().multiplyScalar(5));
        target.y = targetHeight;

        this.camera.lookAt(target);
        position.setY(height);
    }
}

class TopDownControls extends BaseControls {
    constructor(
        canvasDomElement,
        camera,
        cameraAnimation,
        orbitOffset = 4.0,
        defaultFlyAngle = 55.0,
        minFlyAngle = -20.0,
        maxFlyAngle = 55.0,
    ) {
        super(canvasDomElement);
        this.camera = camera;
        this.cameraAnimation = cameraAnimation;
        this.orbitOffset = orbitOffset;
        this.defaultFlyAngle = defaultFlyAngle;
        this.flyAngle = defaultFlyAngle;
        this.minFlyAngle = minFlyAngle;
        this.maxFlyAngle = maxFlyAngle;
        this._orbitTarget = null;
    }

    register(initialTarget = null, animate = true) {
        super.register();
        this.flyAngle = this.defaultFlyAngle;

        if (initialTarget !== null) {
            this._orbitTarget = initialTarget;
            this._orbitTarget.y = 0;

            let direction = this._orbitTarget.clone().sub(this.camera.position);
            direction.y = 0;
            direction = direction.normalize();
            const cameraPosition = this._orbitTarget
                .clone()
                .sub(direction.multiplyScalar(this.orbitOffset))
                .setY(this._getFlyHeight());
            if (animate) {
                this.cameraAnimation.animateLookAt(cameraPosition, this._orbitTarget);
            } else {
                this.camera.position.copy(cameraPosition);
                this.camera.lookAt(this._orbitTarget);
            }
        } else {
            this.camera.position.setY(this._getFlyHeight());
            this._updateOrbitTarget();
            this.camera.lookAt(this._orbitTarget);
        }
    }

    _getFlyHeight() {
        const minHeight = 1.0;
        const maxHeight = 6.0;
        return minHeight + (maxHeight - minHeight) * (this.flyAngle - this.minFlyAngle) / (this.maxFlyAngle - this.minFlyAngle);
    }

    _handleMouseMove(event) {
        super._handleMouseMove(event);
        if (this.cameraAnimation.isPlaying())
            return;
        if (MouseEventUtils.isPressed(event, MouseEventUtils.RIGHT_BTN) ||
            (MouseEventUtils.isPressed(event, MouseEventUtils.LEFT_BTN) && event.altKey)) {
            this._handleOrbiting(this._getMovement(event))
        } else if (MouseEventUtils.isPressed(event, MouseEventUtils.LEFT_BTN)) {
            this._handlePanning(this._getMovement(event));
        } else {
            return;
        }

        event.preventDefault()
        this.dispatchEvent(_changeEvent);
    }

    _handleTouch(event) {
        super._handleTouch(event);
        if (this.cameraAnimation.isPlaying())
            return;

        if (event.pointers.length === 1) {
            this._handlePanning([event.deltaXAdjusted * 3, event.deltaYAdjusted * 3]);
            // TODO: why should this be here?
            // this._handleOrbiting([event.rotationDelta * -5, 0]);
        } else {
            this._handlePanning([event.deltaXAdjusted * 3, 0]);
            this._handleOrbiting([event.rotationDelta * -5, event.deltaYAdjusted * 3]);
        }

        event.preventDefault();
        this.dispatchEvent(_changeEvent);
    }

    _handlePanning(movement) {
        const [deltaX, deltaY] = movement;
        const [right, _, forward] = this._localCoordinateSystem()

        this.camera.position
            .addScaledVector(right, 0.005 * deltaX)
            .addScaledVector(forward, 0.005 * deltaY);
        this._updateOrbitTarget();
    }

    _updateOrbitTarget() {
        this._orbitTarget = this.camera.position.clone().add(this._forwardVector().multiplyScalar(this.orbitOffset));
        this._orbitTarget.y = 0;
    }

    _handleOrbiting(movement) {
        const [deltaX, deltaY] = movement;

        this.flyAngle += deltaY * 0.1;
        this.flyAngle = MathUtils.clamp(this.flyAngle, this.minFlyAngle, this.maxFlyAngle);

        const position = this.camera.position;
        const target = position.clone().add(this._forwardVector().multiplyScalar(this.orbitOffset));
        target.y -= Math.tan(THREE.MathUtils.degToRad(this.flyAngle)) * this.orbitOffset;
        // const target = this._orbitTarget;

        const quat = new THREE.Quaternion().setFromUnitVectors(this.camera.up, new Vector3(0, 1, 0));
        const quatInverse = quat.clone().invert();
        const offset = new THREE.Vector3().subVectors(position, target)
        // rotate offset to "y-axis-is-up" space
        offset.applyQuaternion(quat);

        const spherical = new THREE.Spherical();
        spherical.setFromVector3(offset);

        spherical.theta += -deltaX * 0.003;
        spherical.makeSafe();

        offset.setFromSpherical(spherical);
        // rotate offset back to "camera-up-vector-is-up" space
        offset.applyQuaternion(quatInverse);
        position.copy(target).add(offset);
        this.camera.lookAt(target);
        position.setY(this._getFlyHeight());
    }

    _forwardVector() {
        const direction = new THREE.Vector3();
        this.camera.getWorldDirection(direction);
        direction.y = 0;
        return direction.normalize();
    }
}

class RouteOrbitControls extends BaseControls {
    constructor(canvasDomElement, camera, cameraAnimation) {
        super(canvasDomElement);
        this.camera = camera;
        this.cameraAnimation = cameraAnimation;

        this._orbitTarget = null;
    }

    register(route, wallMesh) {
        this._orbitTarget = route.getCenter();

        const cameraPosition = route.getViewingPoint(wallMesh);
        const viewingDirection = route.getViewingDirection(wallMesh);
        this.cameraAnimation.animateLookAt(cameraPosition, cameraPosition.clone().add(viewingDirection));

        super.register();
    }

    _handleMouseMove(event) {
        super._handleMouseMove(event);
        if (this.cameraAnimation.isPlaying())
            return;

        if (MouseEventUtils.isPressed(event, MouseEventUtils.LEFT_BTN)) {
            const [deltaX, deltaY] = this._getMovement(event);
            if (event.shiftKey) {
                this._handleZoom((deltaX - deltaY) * 2);
            } else {
                this._handleOrbiting([deltaX, deltaY]);
            }
            this.dispatchEvent(_changeEvent);
        }
    }

    _handleMouseWheel(event) {
        super._handleMouseWheel(event);
        if (this.cameraAnimation.isPlaying())
            return;

        event.preventDefault();
        this._handleZoom(event.deltaY);
        this.dispatchEvent(_changeEvent);
    }

    _handleTouch(event) {
        super._handleTouch(event);
        if (this.cameraAnimation.isPlaying())
            return;
        this._handleOrbiting([event.deltaXAdjusted * 4, event.deltaYAdjusted * 4]);
        // TODO: this is bananas
        this._handleZoom((event.scaleDelta - 1) * 2500);

        event.preventDefault();
        this.dispatchEvent(_changeEvent);
    }

    _handleOrbiting([deltaX, deltaY]) {
        const position = this.camera.position;
        const target = this._orbitTarget;

        const quat = new THREE.Quaternion().setFromUnitVectors(this.camera.up, new Vector3(0, 1, 0));
        const quatInverse = quat.clone().invert();
        const offset = new THREE.Vector3().subVectors(position, target);
        // rotate offset to "y-axis-is-up" space
        offset.applyQuaternion(quat);

        const spherical = new THREE.Spherical();
        spherical.setFromVector3(offset);

        spherical.theta += -deltaX * 0.003;
        spherical.phi += -deltaY * 0.003;
        spherical.makeSafe();

        offset.setFromSpherical(spherical);
        // rotate offset back to "camera-up-vector-is-up" space
        offset.applyQuaternion(quatInverse);
        position.copy(target).add(offset);
        this.camera.lookAt(target);
    }

    _handleZoom(delta) {
        const position = this.camera.position;
        const target = this._orbitTarget;

        const offset = new THREE.Vector3().subVectors(position, target);
        const offsetDelta = offset.clone().normalize().multiplyScalar(-delta * 0.001);
        const newOffset = offset.add(offsetDelta).clampLength(2, 6);
        const newPosition = this._orbitTarget.clone().add(newOffset);
        this.camera.position.copy(newPosition);
    }
}

class DebugFlyingControls extends BaseControls {
    constructor(canvasDomElement, camera) {
        super(canvasDomElement);
        this.camera = camera;
    }


    _handleMouseMove(event) {
        super._handleMouseMove(event);
        if (MouseEventUtils.isPressed(event, MouseEventUtils.RIGHT_BTN)) {
            this._handlePanning(event);
            this.dispatchEvent(_changeEvent);
        } else if (MouseEventUtils.isPressed(event, MouseEventUtils.LEFT_BTN)) {
            this._handleTilting(event)
            this.dispatchEvent(_changeEvent);
        }
    }

    _handlePanning(event) {
        const [deltaX, deltaY] = this._getMovement(event);
        const [right, up, _] = this._localCoordinateSystem()

        this.camera.position
            .addScaledVector(right, 0.003 * deltaX)
            .addScaledVector(up, 0.003 * deltaY);
    }

    _handleTilting(event) {
        const [deltaX, deltaY] = this._getMovement(event);

        const camera = this.camera;
        const rotation = new THREE.Euler(0, 0, 0, "YXZ");
        rotation.setFromQuaternion(camera.quaternion);

        rotation.y += deltaX * 0.001;
        rotation.x += deltaY * 0.001;

        const minPolarAngle = 0.05;
        const maxPolarAngle = Math.PI - 0.05;
        rotation.x = Math.max(Math.PI / 2 - maxPolarAngle, Math.min(Math.PI / 2 - minPolarAngle, rotation.x));

        camera.quaternion.setFromEuler(rotation);
    }

    _handleMouseWheel(event) {
        super._handleMouseWheel(event);
        event.preventDefault();

        const [right, _, forward] = this._localCoordinateSystem()
        this.camera.position.addScaledVector(forward, -event.deltaY * 0.001);
        this.camera.position.addScaledVector(right, -event.deltaX * 0.001);

        this.dispatchEvent(_changeEvent);
    }
}

class Raycasting extends BaseControls {
    static Target = class Target {
        constructor(object, parts) {
            this.object = object;
            this.parts = parts;
        }
    }

    static Result = class Result {
        constructor(target, part) {
            this.target = target;
            this.part = part;
            this.hit = target !== null;
        }

        equals(otherResult) {
            return this.target === otherResult.target && this.part === otherResult.part;
        }
    }

    constructor(canvasDomElement, camera) {
        super(canvasDomElement);
        this._camera = camera;
        this._pointerPosition = new THREE.Vector2();

        this.reset();
    }

    addTarget(object, parts) {
        const target = new Raycasting.Target(object, parts)
        this._allParts.push(...parts);
        for (let part of parts) {
            this._partToTarget.set(part, target);
        }
    }

    addApproachTarget(object, spheres) {
        const target = new Raycasting.Target(object, spheres)
        this._allApproachSpheres.push(...spheres);
        for (let sphere of spheres) {
            this._partToTarget.set(sphere, target);
        }
    }

    reset() {
        this._allParts = [];
        this._allApproachSpheres = [];
        this._partToTarget = new Map();
        this._lastResult = new Raycasting.Result(null, null);
        this._lastApproach = new Raycasting.Result(null, null);
    }

    _handleMouseMove(event) {
        super._handleMouseMove(event);
        this._updatePointerPosition(event);

        const result = this._raycast();
        if (!result.equals(this._lastResult)) {
            this.dispatchEvent({..._objectHoverEvent, result: result});
            this._lastResult = result;
        }

        if (!result.hit) {
            const approachResult = this._raycastNearestApproach();
            if (!approachResult.equals(this._lastApproach)) {
                this.dispatchEvent({..._objectApproachEvent, result: approachResult});
                this._lastApproach = approachResult;
            }
        }
    }

    _handleClick(event) {
        super._handleClick(event);
        this._updatePointerPosition(event);

        const result = this._raycast();
        if (result.hit) {
            this.dispatchEvent({..._objectClickEvent, result: result, screen_pointer: this._getScreenPosition(event)});
        }
        if (!result.hit) {
            const approachResult = this._raycastNearestApproach();
            if (approachResult.hit) {
                this.dispatchEvent({..._objectApproachClickEvent, result: approachResult});
            }
        }
    }

    _createRaycaster() {
        const raycaster = new THREE.Raycaster();
        raycaster.params.Line.threshold = 0.05;
        raycaster.setFromCamera(this._pointerPosition, this._camera);
        return raycaster;
    }

    _raycast() {
        const raycaster = this._createRaycaster();
        const intersects = raycaster.intersectObjects(this._allParts);
        const intersect = intersects.length === 0 ? null : intersects[0].object;

        if (intersect === null)
            return new Raycasting.Result(null, null);
        const target = this._partToTarget.get(intersect);
        return new Raycasting.Result(target, intersect);
    }

    _raycastNearestApproach() {
        const raycaster = this._createRaycaster();
        const ray = raycaster.ray;

        let minDistance = null;
        let approachedPart = null;
        for (let sphere of this._allApproachSpheres) {
            const distance = ray.distanceSqToPoint(sphere.center);
            if (distance > sphere.radius)
                continue;
            if (minDistance === null || distance < minDistance) {
                minDistance = distance;
                approachedPart = sphere;
            }
        }
        if (approachedPart === null)
            return new Raycasting.Result(null, null);
        const target = this._partToTarget.get(approachedPart);
        return new Raycasting.Result(target, approachedPart);
    }

    _updatePointerPosition(event) {
        this._pointerPosition.x = (event.clientX / window.innerWidth) * 2 - 1;
        this._pointerPosition.y = -(event.clientY / window.innerHeight) * 2 + 1;
    }

    _getScreenPosition(event) {
        return new THREE.Vector2(event.clientX, event.clientY);
    }
}

export {DebugFlyingControls, RouteOrbitControls, TopDownControls, TopDownControlsV2, Raycasting};